/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { useEffect, useState } from "react";
import MDButton from "components/MDButton";
import { Icon } from "@mui/material";
import Swal from "sweetalert2";
import ExternalGoodsSearchDialog from "./Components/ExternalGoodsSearchDialog";
import DataTable from "components/ui/Tables/DataTable";
import { request } from "app/network";
import ApiRegistry from "app/network/api";
import EditDisplayItemDialog from "./Components/EditDisplayItemDialog";
import ReorderDisplayItemsDialog from "./Components/ReorderDisplayItemsDialog";
import ProductDetailDialog from "./Components/ProductDetailDialog";
import { useMaterialUIController } from "context";
import { useQueryClient } from "@tanstack/react-query";

function GifticonProductListScreen(): JSX.Element {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const queryClient = useQueryClient();
    const [openDialog, setOpenDialog] = useState(false);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [rawData, setRawData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Edit Dialog State
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedItemForEdit, setSelectedItemForEdit] = useState<any>(null);
    const [reorderDialogOpen, setReorderDialogOpen] = useState(false);

    // Detail Dialog State
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedDetailCode, setSelectedDetailCode] = useState<string | null>(null);

    const [bizMoney, setBizMoney] = useState<string>("0");

    useEffect(() => {
        fetchList();
        fetchBizMoney();
    }, [page]);

    const fetchBizMoney = async () => {
        try {
            const res = await request("Giftishow.GetBizMoney", {});
            if (res && res.data && res.data.result) {
                setBizMoney(res.data.result.balance);
            }
        } catch (error) {
            console.error("Failed to fetch BizMoney", error);
        }
    };

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await request("Giftishow.GetDisplayList", {
                paramz: {
                    start: page,
                    size: 20
                }
            });
            // Note: ApiRegistry definitions usually are passed to request(apiName, options) or request(apiObject, options)
            // Depending on implementation of 'request' in networkManager. Usually it takes string key or object.
            // Based on SmsHistoryScreen: request("Sms.GetHistory", { paramz... })
            // So we should try request("Giftishow.GetDisplayList", ...)

            if (res && res.data) {
                const listResult = res.data;
                setTotalCount(listResult.totalCount || 0);
                setRawData(listResult.list || []);
            }
        } catch (error) {
            console.error("Failed to load list", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item: any) => {
        setSelectedItemForEdit(item);
        setEditDialogOpen(true);
    };

    const handleEditSuccess = () => {
        fetchList(); // Refresh list after edit
    };

    const handleDelete = async (item: any) => {
        const result = await Swal.fire({
            title: "상품 삭제",
            text: "정말 이 상품을 목록에서 삭제하시겠습니까?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "삭제",
            cancelButtonText: "취소",
            confirmButtonColor: "#f44336"
        });

        if (result.isConfirmed) {
            try {
                await request(`Giftishow.DeleteDisplayItem`, {
                    pathParams: { displaySeq: item.displaySeq }
                });
                Swal.fire("삭제 완료", "상품이 목록에서 삭제되었습니다.", "success");
                queryClient.invalidateQueries({ queryKey: ["externalGoods"] }); // Invalidate search cache
                fetchList(); // Refresh
            } catch (error: any) {
                console.error("Delete failed", error);
                Swal.fire("오류", error.message || "삭제 중 오류가 발생했습니다.", "error");
            }
        }
    };

    const columns = [
        { Header: "상품 정보", accessor: "product", width: "40%" },
        { Header: "가격", accessor: "price", align: "right" },
        { Header: "전시 순서", accessor: "displayOrder", align: "center" },
        { Header: "상태", accessor: "status", align: "center" },
        { Header: "동기화", accessor: "syncedStatus", align: "center" }
    ];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox py={3}>
                <MDBox mb={3}>
                    <Card>
                        <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox lineHeight={1}>
                                <MDTypography variant="h5" fontWeight="medium">
                                    상품 관리 (전시 관리)
                                </MDTypography>
                                <MDTypography variant="button" color="text">
                                    기프티콘 상품 목록을 관리하는 화면입니다.
                                </MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={2}>
                                <MDBox bgcolor={darkMode ? "#202940" : "#f8f9fa"} px={2} py={1} borderRadius="lg" border={`1px solid ${darkMode ? "rgba(255,255,255,0.1)" : "#e0e0e0"}`}>
                                    <MDTypography variant="caption" color="text" fontWeight="bold" mr={1}>비즈머니 잔액</MDTypography>
                                    <MDTypography variant="button" color="info" fontWeight="bold">
                                        {Number(bizMoney).toLocaleString()} 원
                                    </MDTypography>
                                </MDBox>
                                <MDButton variant="outlined" color="info" onClick={() => setReorderDialogOpen(true)} sx={{ mr: 1 }}>
                                    <Icon sx={{ fontWeight: "bold" }}>sort</Icon>
                                    &nbsp;순서변경
                                </MDButton>
                                <MDButton variant="gradient" color="dark" onClick={() => setOpenDialog(true)}>
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;상품 추가
                                </MDButton>
                            </MDBox>
                        </MDBox>

                        <MDBox p={3}>
                            {loading ? (
                                <MDBox display="flex" justifyContent="center" p={5}>
                                    <Icon sx={{ animation: "spin 2s linear infinite" }}>loop</Icon>
                                </MDBox>
                            ) : (
                                <Grid container spacing={3}>
                                    {rawData.map((item: any) => (
                                        <Grid item xs={12} sm={6} md={3} lg={2.4} key={item.displaySeq}>
                                            <Card sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                borderRadius: "12px",
                                                boxShadow: "0px 4px 6px rgba(0,0,0,0.05)",
                                                border: "1px solid rgba(0,0,0,0.05)",
                                                transition: "all 0.2s ease-in-out",
                                                cursor: "pointer",
                                                "&:hover": {
                                                    transform: "translateY(-5px)",
                                                    boxShadow: "0px 12px 20px rgba(0,0,0,0.1)",
                                                }
                                            }}
                                                onClick={() => {
                                                    setSelectedDetailCode(item.goodsCode);
                                                    setDetailDialogOpen(true);
                                                }}
                                            >
                                                <MDBox position="relative">
                                                    <MDBox sx={{
                                                        bgcolor: "#fff",
                                                        p: 2,
                                                        borderRadius: "12px 12px 0 0",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        height: "200px", // Fixed height for container
                                                        overflow: "hidden"
                                                    }}>
                                                        <CardMedia
                                                            component="img"
                                                            image={item.goodsImgS}
                                                            alt={item.goodsName}
                                                            sx={{
                                                                maxWidth: "100%",
                                                                maxHeight: "100%",
                                                                width: "auto",
                                                                height: "auto",
                                                                objectFit: "contain",
                                                                display: "block"
                                                            }}
                                                        />
                                                    </MDBox>
                                                    <MDBox
                                                        position="absolute"
                                                        top={10}
                                                        left={10}
                                                        bgcolor="rgba(255, 255, 255, 0.6)"
                                                        px={1}
                                                        py={0.5}
                                                        borderRadius="8px"
                                                        border="1px solid rgba(255, 255, 255, 0.8)"
                                                        sx={{ backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)" }}
                                                    >
                                                        <MDTypography variant="caption" color="secondary" fontWeight="bold">
                                                            {item.brandName}
                                                        </MDTypography>
                                                    </MDBox>
                                                    {/* Category Badge */}
                                                    {item.categoryName && (
                                                        <MDBox position="absolute" bottom={10} left={10}>
                                                            <MDTypography variant="caption" fontWeight="bold"
                                                                sx={{
                                                                    bgcolor: "rgba(0,0,0,0.6)",
                                                                    color: "#fff",
                                                                    px: 1,
                                                                    py: 0.5,
                                                                    borderRadius: "4px"
                                                                }}>
                                                                {item.categoryName}
                                                            </MDTypography>
                                                        </MDBox>
                                                    )}

                                                    {/* Status Badge */}
                                                    <MDBox position="absolute" top={10} right={10}>
                                                        <MDTypography variant="caption" fontWeight="bold"
                                                            sx={{
                                                                bgcolor: item.isActive ? "#4CAF50" : "#F44336",
                                                                color: "#fff",
                                                                px: 1,
                                                                py: 0.5,
                                                                borderRadius: "4px"
                                                            }}>
                                                            {item.isActive ? "노출중" : "숨김"}
                                                        </MDTypography>
                                                    </MDBox>
                                                </MDBox>

                                                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 2 }}>
                                                    <MDTypography variant="subtitle2" sx={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        minHeight: "40px",
                                                        mb: 1,
                                                        lineHeight: 1.3
                                                    }}>
                                                        {item.goodsName}
                                                    </MDTypography>

                                                    <MDBox mt="auto" display="flex" alignItems="center" justifyContent="space-between">
                                                        <MDBox>
                                                            <MDTypography variant="h6" color="info" fontWeight="bold">
                                                                {Number(item.salePrice).toLocaleString()}
                                                                <MDTypography component="span" variant="caption" color="text">원</MDTypography>
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="secondary" fontWeight="bold">
                                                                차감: {Number(item.requiredPoint).toLocaleString()} P
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                </CardContent>
                                                <MDBox p={2} pt={0} display="flex" justifyContent="flex-end">
                                                    <MDBox display="flex" gap={0.5}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(item);
                                                            }}
                                                            sx={{
                                                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                                                "&:hover": {
                                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                                                    color: "#49a3f1",
                                                                },
                                                            }}
                                                        >
                                                            <Icon fontSize="small">edit</Icon>
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(item);
                                                            }}
                                                            sx={{
                                                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                                                "&:hover": {
                                                                    backgroundColor: darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                                                    color: "#f44336",
                                                                },
                                                            }}
                                                        >
                                                            <Icon fontSize="small">delete</Icon>
                                                        </IconButton>
                                                    </MDBox>
                                                </MDBox>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </MDBox>
                    </Card>
                </MDBox>
            </MDBox>
            <Footer />
            <ExternalGoodsSearchDialog open={openDialog} onClose={() => { setOpenDialog(false); fetchList(); }} />
            <EditDisplayItemDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                item={selectedItemForEdit}
                onSuccess={handleEditSuccess}
                darkMode={darkMode}
            />
            <ReorderDisplayItemsDialog
                open={reorderDialogOpen}
                onClose={() => setReorderDialogOpen(false)}
                onSuccess={() => fetchList()}
                darkMode={darkMode}
            />
            <ProductDetailDialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                goodsCode={selectedDetailCode}
                darkMode={darkMode}
            />
        </DashboardLayout >
    );
}

export default GifticonProductListScreen;
